import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldAlert, CheckCircle2, Clock, KeyRound } from "lucide-react";
import { formatDate } from "@/lib/checklist";

/**
 * NMCPanel — for Registered Nurses:
 * - Worker (self) can set PIN once
 * - Reviewers (team/manager) can also set PIN and log monthly register checks
 * - Shows history of checks
 */
export default function NMCPanel({ worker, nmc, canReview, onChanged }) {
  const [pin, setPin] = useState(nmc?.pin || "");
  const [history, setHistory] = useState([]);
  const [checkOpen, setCheckOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get(`/workers/${worker.id}/nmc-checks`);
      setHistory(data);
    } catch (_e) {
      /* ignore */
    }
  }, [worker.id]);

  useEffect(() => {
    if (nmc?.pin) loadHistory();
  }, [nmc?.pin, loadHistory]);

  const savePin = async () => {
    if (!pin.trim()) {
      toast.error("Enter a valid PIN");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/workers/${worker.id}/nmc-pin`, { nmc_pin: pin.trim() });
      toast.success("NMC PIN saved");
      onChanged && onChanged();
    } catch (e) {
      toast.error("Could not save PIN", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  const statusMeta = {
    approved: { label: "Check up to date", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
    action_required: { label: nmc?.last_check ? "Monthly check due" : "First check due", cls: "bg-rose-50 text-rose-700 border-rose-200", Icon: ShieldAlert },
    missing: { label: "PIN not recorded", cls: "bg-slate-100 text-slate-700 border-slate-200", Icon: KeyRound },
  }[nmc?.status || "missing"];

  const StatusIcon = statusMeta.Icon;

  return (
    <div data-testid="nmc-panel" className="rounded-xl border border-slate-200 bg-white p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md bg-teal-50 border border-teal-100 flex items-center justify-center">
            <KeyRound className="h-5 w-5 text-teal-700" />
          </div>
          <div>
            <div className="label-caps text-slate-500">NMC monitoring</div>
            <div className="font-heading text-lg font-semibold text-slate-900">Nursing &amp; Midwifery Council</div>
            <div className="text-sm text-slate-600 mt-1">Register PIN + a monthly manual check on the NMC website.</div>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${statusMeta.cls}`} data-testid="nmc-status">
          <StatusIcon className="h-3.5 w-3.5" />
          {statusMeta.label}
        </span>
      </div>

      <div className="mt-5 grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-slate-200 p-4">
          <div className="label-caps text-slate-500">NMC PIN</div>
          <div className="mt-2 flex items-center gap-2">
            <Input
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="e.g. 12A3456B"
              data-testid="nmc-pin-input"
              className="max-w-[200px]"
              disabled={!!nmc?.pin && !canReview}
            />
            {(!nmc?.pin || canReview) && (
              <Button size="sm" onClick={savePin} disabled={busy} data-testid="nmc-pin-save" className="bg-teal-700 hover:bg-teal-800">
                {nmc?.pin ? "Update" : "Save"}
              </Button>
            )}
          </div>
          {nmc?.pin && (
            <div className="mt-2 text-xs text-slate-500">On file: <span className="font-mono">{nmc.pin}</span></div>
          )}
        </div>

        <div className="rounded-lg border border-slate-200 p-4">
          <div className="label-caps text-slate-500">Last register check</div>
          {nmc?.last_check ? (
            <>
              <div className="mt-2 text-slate-900 text-sm">{formatDate(nmc.last_check.checked_at)}</div>
              <div className="text-xs text-slate-500 mt-0.5">by {nmc.last_check.checked_by_name}</div>
              <div className="mt-2">
                {nmc.last_check.restrictions_found ? (
                  <span className="inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold bg-rose-50 text-rose-700 border-rose-200">
                    Restrictions found
                  </span>
                ) : (
                  <span className="inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold bg-emerald-50 text-emerald-700 border-emerald-200">
                    Clean check
                  </span>
                )}
              </div>
            </>
          ) : (
            <div className="mt-2 text-sm text-slate-500 flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" /> No check recorded yet.
            </div>
          )}
        </div>
      </div>

      {canReview && nmc?.pin && (
        <div className="mt-4">
          <Button onClick={() => setCheckOpen(true)} data-testid="nmc-log-check-btn" className="bg-slate-900 hover:bg-slate-800">
            Log monthly check
          </Button>
        </div>
      )}

      {history.length > 0 && (
        <details className="mt-5" data-testid="nmc-history">
          <summary className="text-sm font-semibold text-slate-700 cursor-pointer">
            View history ({history.length})
          </summary>
          <div className="mt-3 space-y-2">
            {history.map((h) => (
              <div key={h.id} className="rounded-md border border-slate-200 p-3 text-sm">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="font-medium text-slate-900">{formatDate(h.checked_at)}</span>
                    <span className="text-slate-500"> · {h.checked_by_name}</span>
                  </div>
                  <span
                    className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${
                      h.restrictions_found
                        ? "bg-rose-50 text-rose-700 border-rose-200"
                        : "bg-emerald-50 text-emerald-700 border-emerald-200"
                    }`}
                  >
                    {h.restrictions_found ? "Restrictions" : "Clean"}
                  </span>
                </div>
                {h.notes && <div className="text-slate-600 mt-1">{h.notes}</div>}
              </div>
            ))}
          </div>
        </details>
      )}

      <LogCheckDialog
        open={checkOpen}
        onOpenChange={setCheckOpen}
        workerId={worker.id}
        onSubmitted={() => {
          loadHistory();
          onChanged && onChanged();
        }}
      />
    </div>
  );
}

function LogCheckDialog({ open, onOpenChange, workerId, onSubmitted }) {
  const [restrictions, setRestrictions] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setRestrictions(false);
      setNotes("");
    }
  }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/workers/${workerId}/nmc-check`, { restrictions_found: restrictions, notes });
      toast.success("Check recorded");
      onOpenChange(false);
      onSubmitted && onSubmitted();
    } catch (e) {
      toast.error("Could not record check", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white sm:max-w-md" data-testid="nmc-check-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Log NMC register check</DialogTitle>
          <DialogDescription>
            Confirm you have just verified this nurse on the public NMC register.
          </DialogDescription>
        </DialogHeader>
        <label className="flex items-start gap-3 rounded-md border border-slate-200 p-3 cursor-pointer">
          <Checkbox checked={restrictions} onCheckedChange={setRestrictions} data-testid="nmc-restrictions-checkbox" />
          <div>
            <div className="text-sm font-semibold text-slate-900">Restrictions on practice were found</div>
            <div className="text-xs text-slate-500 mt-0.5">Tick this box only if the NMC register shows conditions, cautions, or suspensions.</div>
          </div>
        </label>
        <div className="space-y-1.5">
          <Label className="label-caps">Notes (optional)</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="nmc-notes" placeholder="Any observations from the register check…" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="nmc-submit-check" className="bg-teal-700 hover:bg-teal-800">
            {busy ? "Saving…" : "Save check"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
